import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, onSnapshot, deleteField } from 'firebase/firestore';
import { db } from '../services/firebase';
import { toast } from 'react-toastify';
import DailyIframe from '@daily-co/daily-js';
import './VideoRoom.css';

const VideoRoom = () => {
  const { bookingId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const dailyRef = useRef(null);
  const videoContainerRef = useRef(null);

  const [booking, setBooking] = useState(null);
  const [isInCall, setIsInCall] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Time status state
  const [timeStatus, setTimeStatus] = useState({ canJoin: false, message: 'Loading...' });

  // GÜVENLİK GÜNCELLEMESİ: API Anahtarı buradan kaldırıldı. Artık sadece subdomain kullanılıyor.
  const DAILY_SUBDOMAIN = process.env.REACT_APP_DAILY_SUBDOMAIN; 

  // 1. Real-time Listener
  useEffect(() => {
    if (!currentUser || !bookingId) return;
    
    const unsubscribe = onSnapshot(doc(db, 'bookings', bookingId), (docSnap) => {
      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        
        // Security Check
        if (data.studentId !== currentUser.uid && data.teacherId !== currentUser.uid) {
          toast.error("You are not authorized to access this lesson.");
          navigate('/');
          return;
        }
        
        setBooking(data);
      } else {
        navigate('/my-lessons');
      }
    });
    
    return () => {
        unsubscribe();
        if (dailyRef.current) dailyRef.current.destroy();
    };
  }, [bookingId, currentUser, navigate]);

  // 2. Time Check (10-Minute Rule)
  useEffect(() => {
    if (!booking) return;

    const checkTime = () => {
        // If lesson already started or link exists, bypass time check (for reconnection)
        if (booking.status === 'in_progress' || booking.meetingLink) {
            setTimeStatus({ canJoin: true, message: 'Lesson Active' });
            return;
        }

        const lessonStart = new Date(`${booking.date}T${booking.startTime}`);
        const now = new Date();
        const diffMinutes = (lessonStart - now) / 1000 / 60;

        if (diffMinutes > 10) {
            setTimeStatus({ 
                canJoin: false, 
                message: `Class starts in ${Math.round(diffMinutes)} minutes. Entry opens 10 mins before start.` 
            });
        } else if (diffMinutes < -60) { 
             setTimeStatus({ canJoin: true, message: 'Lesson time passed, but you can still join.' });
        } else {
            setTimeStatus({ canJoin: true, message: 'You can join now.' });
        }
    };

    checkTime();
    const timer = setInterval(checkTime, 60000);
    return () => clearInterval(timer);

  }, [booking]);

  // Actions
  const confirmLesson = async () => {
    setProcessing(true);
    try {
      await updateDoc(doc(db, 'bookings', bookingId), { status: 'confirmed' });
      toast.success("Lesson confirmed!");
    } catch (e) { toast.error("An error occurred"); }
    finally { setProcessing(false); }
  };

  const startLesson = async () => {
    if (!timeStatus.canJoin) {
        toast.warning(timeStatus.message);
        return;
    }

    setProcessing(true);
    try {
        if (!DAILY_SUBDOMAIN) {
            throw new Error("Daily.co subdomain is missing in environment variables (.env).");
        }
        
        // --- GÜVENLİK GÜNCELLEMESİ: Daily.co API Anahtarı gizlenmiştir. ---
        // Oda oluşturma işlemi artık doğrudan Daily.co API'si yerine güvenli bir backend/Cloud Function üzerinden yapılacaktır.
        // Cloud Function'ınız bu POST isteğini almalı, Daily.co API'sini kendi gizli anahtarıyla çağırmalı ve roomUrl'yi döndürmelidir.
        
        const response = await fetch('/api/create-daily-room', { // 🚨 NOT: Bu URL (örn: /api/create-daily-room) sizin backend/Cloud Function adresiniz olmalıdır!
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                // API Anahtarı artık frontend'den gönderilmiyor.
            },
            body: JSON.stringify({
                bookingId: bookingId, // Backend'in kullanacağı benzersiz bir kimlik
                exp: 7200,            // Oda süresi (2 saat)
                // İhtiyacınız olan diğer odası ayarları da burada gönderilebilir.
            })
        });

        const data = await response.json();
        
        // Hata kontrolü ve URL alma
        if (!response.ok || !data.roomUrl || !data.roomName) {
            throw new Error(data.message || "Failed to create meeting room securely (Check backend logs).");
        }
        
        const roomUrl = data.roomUrl;

        await updateDoc(doc(db, 'bookings', bookingId), {
            meetingLink: roomUrl,
            roomName: data.roomName,
            status: 'in_progress'
        });
        
        joinCall(roomUrl);
    } catch (e) { toast.error("Error: " + e.message); }
    finally { setProcessing(false); }
  };

  const joinCall = async (url) => {
    if (dailyRef.current) return;
    try {
        const daily = DailyIframe.createFrame(videoContainerRef.current, {
            iframeStyle: { width: '100%', height: '100%', border: '0', backgroundColor: '#202124' },
            showLeaveButton: true,
            showFullscreenButton: true,
        });
        dailyRef.current = daily;
        daily.on('joined-meeting', () => setIsInCall(true));
        daily.on('left-meeting', () => {
            if(dailyRef.current) dailyRef.current.destroy();
            dailyRef.current = null;
            setIsInCall(false);
            window.location.reload(); 
        });
        await daily.join({ url: url });
    } catch (e) { toast.error("Join error: " + e.message); }
  };

  const resetRoom = async () => {
      if(!window.confirm("Are you sure you want to reset the room?")) return;
      await updateDoc(doc(db, 'bookings', bookingId), {
          meetingLink: deleteField(),
          roomName: deleteField(),
          status: 'confirmed'
      });
      window.location.reload();
  };

  if (!booking) return <div className="loading-container"><div className="spinner"></div></div>;

  return (
    <div className="video-room-container">
      {/* Video Frame (Fixed on top) */}
      <div ref={videoContainerRef} style={{ 
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', 
            zIndex: 99999, backgroundColor: '#202124', display: isInCall ? 'block' : 'none' 
        }} />

      {/* Lobby / Waiting Room */}
      {!isInCall && (
        <div className="container py-5" style={{ marginTop: '80px' }}>
            <div className="card shadow-lg p-4 mx-auto" style={{maxWidth: '800px'}}>
                <div className="text-center mb-4">
                    <h2 className="fw-bold text-primary">🎓 {booking.subject || 'Online'} Lesson</h2>
                    <p className="text-muted">
                        📅 {booking.date} | ⏰ {booking.startTime}
                    </p>
                    
                    {/* Time Warning */}
                    {!timeStatus.canJoin && booking.status !== 'in_progress' && (
                        <div className="alert alert-warning">
                            ⏳ {timeStatus.message}
                        </div>
                    )}

                    {/* Status Badge */}
                    <span className={`badge p-2 ${
                        booking.status === 'in_progress' ? 'bg-danger' : 
                        booking.status === 'confirmed' ? 'bg-success' : 'bg-warning text-dark'
                    }`}>
                        {booking.status === 'in_progress' ? 'LIVE NOW' : 
                         booking.status === 'confirmed' ? 'CONFIRMED' : 'PENDING APPROVAL'}
                    </span>
                </div>

                <div className="d-grid gap-3">
                    {/* 1. Teacher Actions: Confirm */}
                    {userProfile?.role === 'teacher' && booking.status === 'pending' && (
                        <button onClick={confirmLesson} disabled={processing} className="btn btn-success btn-lg">
                            ✅ Confirm Lesson
                        </button>
                    )}

                    {/* 2. Teacher Actions: Start */}
                    {userProfile?.role === 'teacher' && booking.status === 'confirmed' && !booking.meetingLink && (
                        <button 
                            onClick={startLesson} 
                            disabled={processing || !timeStatus.canJoin} 
                            className="btn btn-primary btn-lg"
                        >
                            {processing ? 'Preparing Room...' : '🚀 Start Lesson'}
                        </button>
                    )}

                    {/* 3. Join / Reconnect */}
                    {(booking.status === 'in_progress' || booking.meetingLink) && (
                        <div className="text-center">
                            <div className="alert alert-success mb-3">
                                🟢 <strong>Lesson Active!</strong> 
                                <br/>Click below to join or reconnect if disconnected.
                            </div>
                            <button 
                                onClick={() => joinCall(booking.meetingLink)} 
                                className="btn btn-success btn-lg py-3 w-100 pulsing-button"
                            >
                                {isInCall ? 'Return to Video' : '🎥 JOIN LESSON / RECONNECT'}
                            </button>
                        </div>
                    )}

                    {/* Back Button */}
                    <button onClick={() => navigate('/my-lessons')} className="btn btn-outline-secondary mt-2">
                        ← Back to List
                    </button>

                    {/* Emergency Reset */}
                    {userProfile?.role === 'teacher' && booking.meetingLink && (
                         <button onClick={resetRoom} className="btn btn-link text-danger btn-sm mt-3" style={{textDecoration:'none'}}>
                            ⚠️ Technical issues? Reset Room
                        </button>
                    )}
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default VideoRoom;